// IntegrationChecklistTable.tsx
import RefreshIcon from '@mui/icons-material/Refresh'
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Slide,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useBatchUpdateItems } from 'api/aws/batch/useBatchUpdateItems'
import { useBatchUpdateChecklists } from 'api/aws/checklists/useBatchUpdateChecklists'
import { useSyncQueryCache } from 'api/aws/organizations_table/useSyncQueryCache'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useLayoutContext } from 'context/LayoutProvider/LayoutProvider'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { BoxScroller } from 'styles/elements/styleElements'
import type { Checklist, OrganizationTableItem, User } from 'types'
import IntegrationFields from './IntegrationFields'

type Props = {
  deal: OrganizationTableItem
  organization: OrganizationTableItem
  formData?: { [key: string]: any }
  setFormData?: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>
  /** “Integrations” docs already filtered in the drawer */
  checklistData: Checklist[]
  /** parent height toggle */
  expanded: boolean
  /** (optional) allow parent to mirror local patches into its own `checklists` state */
  onMirrorChecklists?: (producer: (prev: Checklist[]) => Checklist[]) => void
  onSaved?: () => void
  type?: string
}
function findItemIndexById(lists: Checklist[], pk: string, sk: string, sectionIndex: number, itemId: string) {
  const checklist = lists.find(ch => ch.PK === pk && ch.SK === sk)
  if (!checklist) return -1
  const section = (checklist.sections || []).find(s => (s.sectionIndex ?? 0) === sectionIndex)
  if (!section) return -1
  const idx = (section.items || []).findIndex(it => it.id === itemId)
  return idx
}

export default function IntegrationChecklistTable({
  deal,
  organization,
  formData,
  setFormData,
  checklistData,
  expanded,
  onMirrorChecklists,
  onSaved,
  type,
}: Props) {
  const qc = useQueryClient()
  const { pinned } = useLayoutContext()
  const { mutateAsync: batchUpdate } = useBatchUpdateChecklists()
  const { mutateAsync: batchUpdateItems } = useBatchUpdateItems(['organization', organization?.PK])
  const { syncOrganizationsCache } = useSyncQueryCache()
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}')
  const { data: users } = useUserItems()
  const { IS_INTEGRATION, IS_SEO } = useMemo<{
    IS_INTEGRATION: boolean
    IS_SEO: boolean
  }>(() => {
    const listIds: string[] = Array.isArray(deal?.onboardingTeamIds) ? deal?.onboardingTeamIds : []
    const list: User[] = users?.filter(u => listIds?.includes(u?.user_uuid))
    const pick = (role: string) =>
      list.filter(m => String(m?.role ?? '').toUpperCase() === role)?.some(u => u?.user_uuid === currentUser?.user_uuid)

    return {
      IS_INTEGRATION: pick('INTEGRATION') || pick('DEV'),
      IS_SEO: pick('SEO'),
    }
  }, [deal?.onboardingTeamIds, users])

  ////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ===== Field state (LOCAL) =====
  const [fieldPristine, setFieldPristine] = useState(formData ?? {})
  const [fieldDraft, setFieldDraft] = useState(formData ?? {})
  const [pending, setPending] = useState<
    {
      PK: string
      SK: string
      itemId: string
      sectionIndex: number
      itemIndex: number
      newStatus: string
    }[]
  >([])
  useEffect(() => {
    setFieldPristine(formData ?? {})
    setFieldDraft(formData ?? {})
  }, [organization?.PK, organization?.SK, formData])
  const handleFieldInputChange = (fieldName: string, newValue: any) => {
    setFieldDraft(prev => ({ ...prev, [fieldName]: newValue }))
  }

  const draftDirtyKeys = useMemo(
    () => Object.keys(fieldDraft ?? {}).filter(k => (fieldDraft?.[k] ?? '') !== (fieldPristine?.[k] ?? '')),
    [fieldDraft, fieldPristine]
  )
  // resync from props if clean
  useEffect(() => {
    if (!formData) return
    if (draftDirtyKeys.length === 0) {
      setFieldPristine(formData)
      setFieldDraft(formData)
    }
  }, [formData, draftDirtyKeys.length])

  const changeCount = pending.length + draftDirtyKeys.length
  const dirty = changeCount > 0
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////
  ////////////////////////////////////////////////////////////////////////////////////////////////////////////

  // Keep pristine copy for cancel/rollback
  const pristineRef = useRef<Checklist[]>(checklistData ?? [])
  useEffect(() => {
    pristineRef.current = checklistData ?? []
  }, [checklistData])

  // Local working copy
  const [localLists, setLocalLists] = useState<Checklist[]>(checklistData ?? [])
  useEffect(() => {
    setLocalLists(checklistData ?? [])
  }, [checklistData])

  // Pending updates (deduped by PK+SK+itemId)

  // Helper: dedupe pending by (PK,SK,itemId)
  const upsertPending = (
    u: { PK: string; SK: string; itemId: string },
    patch: Omit<typeof pending[number], 'PK' | 'SK' | 'itemId'>
  ) => {
    setPending(prev => {
      const idx = prev.findIndex(p => p.PK === u.PK && p.SK === u.SK && p.itemId === u.itemId)
      const next = [...prev]
      const rec = { PK: u.PK, SK: u.SK, itemId: u.itemId, ...patch }
      if (idx === -1) next.push(rec)
      else next[idx] = rec
      return next
    })
  }

  // Flatten rows (preserve section structure for group toggles)
  const rows = useMemo(() => {
    const out: Array<{
      pk: string
      sk: string
      sectionTitle: string
      sectionIndex: number
      itemId: string
      itemIndex: number
      title: string
      done: boolean
      dependencies: string[]
    }> = []
    for (const chk of localLists ?? []) {
      for (const section of chk.sections ?? []) {
        const sIdx = section.sectionIndex ?? 0
        for (const it of section.items ?? []) {
          out.push({
            pk: chk.PK,
            sk: chk.SK,
            sectionTitle: section.title,
            sectionIndex: sIdx,
            itemId: it.id,
            itemIndex: it.itemIndex ?? 0,
            title: it.title,
            done: it.status === 'Done',
            dependencies: Array.isArray(it.dependencies) ? it.dependencies : [],
          })
        }
      }
    }
    out.sort((a, b) =>
      a.pk !== b.pk
        ? a.pk < b.pk
          ? -1
          : 1
        : a.sectionIndex !== b.sectionIndex
        ? a.sectionIndex - b.sectionIndex
        : a.itemIndex - b.itemIndex
    )
    return out
  }, [localLists])

  const sections = useMemo(() => {
    const map = new Map<string, { title: string; key: string; rows: typeof rows }>()
    rows.forEach(r => {
      const key = `${r.pk}|${r.sk}|${r.sectionIndex}`
      if (!map.has(key)) map.set(key, { title: r.sectionTitle, key, rows: [] as any })
      map.get(key)!.rows.push(r)
    })
    return Array.from(map.values())
  }, [rows])

  const progress = useMemo(() => {
    const total = rows.length
    const done = rows.filter(r => r.done).length
    return { total, done, pct: total ? Math.round((done / total) * 100) : 0 }
  }, [rows])

  // ======= cache patch helpers (so overallProgress reacts immediately) =======
  /** Patch the *React Query* cache (all 'checklists*' queries) with a producer */
  const patchChecklistsInCache = (producer: (prev: any) => any) => {
    // update any query whose key starts with 'checklists'
    //@ts-ignore
    qc.getQueryCache()
      .findAll({ predicate: q => Array.isArray(q.queryKey) && String(q.queryKey[0]).includes('checklists') })
      .forEach(q => qc.setQueryData(q.queryKey, (prev: any) => producer(prev)))
  }

  /** Apply the same producer to parent `checklists` state if provided */
  const mirrorParent = (producer: (prev: Checklist[]) => Checklist[]) => {
    onMirrorChecklists?.(producer)
  }
  const keyOf = (ch: Checklist) => `${ch.PK}|${ch.SK}`

  /** Merge-restore ONLY the subset we own into a larger list of checklists */
  const mergeRestoreSubset = (prevLists: Checklist[] = [], subset: Checklist[] = []) => {
    const subsetMap = new Map(subset.map(ch => [keyOf(ch), ch]))
    return (prevLists ?? []).map(ch => {
      const k = keyOf(ch)
      return subsetMap.has(k) ? subsetMap.get(k)! : ch
    })
  }

  /** Mirror parent by merging subset (never replace whole array) */
  const mirrorParentMerge = (subset: Checklist[]) => {
    onMirrorChecklists?.((prev: Checklist[] = []) => mergeRestoreSubset(prev, subset))
  }
  /** Toggle a single item (optimistic in local + cache, mark pending) */
  const toggleItemLocalEverywhere = (row: any, nextDone: boolean) => {
    const nextStatus = nextDone ? 'Done' : 'Not Started'

    // Local
    setLocalLists(prev =>
      prev.map(ch =>
        ch.PK !== row.pk || ch.SK !== row.sk
          ? ch
          : {
              ...ch,
              sections: (ch.sections ?? []).map((sec, sIdx) =>
                (sec.sectionIndex ?? sIdx) !== row.sectionIndex
                  ? sec
                  : {
                      ...sec,
                      items: (sec.items ?? []).map(it => (it.id === row.itemId ? { ...it, status: nextStatus } : it)),
                    }
              ),
            }
      )
    )

    // React Query cache (so overallProgress updates now)
    patchChecklistsInCache((prevLists: Checklist[] = []) =>
      (prevLists ?? []).map(ch =>
        ch.PK !== row.pk || ch.SK !== row.sk
          ? ch
          : {
              ...ch,
              sections: (ch.sections ?? []).map((sec, sIdx) =>
                (sec.sectionIndex ?? sIdx) !== row.sectionIndex
                  ? sec
                  : {
                      ...sec,
                      items: (sec.items ?? []).map(it => (it.id === row.itemId ? { ...it, status: nextStatus } : it)),
                    }
              ),
            }
      )
    )

    // Mirror parent state if they want to track derived totals from their own state
    mirrorParent((prevLists: Checklist[]) =>
      (prevLists ?? []).map(ch =>
        ch.PK !== row.pk || ch.SK !== row.sk
          ? ch
          : {
              ...ch,
              sections: (ch.sections ?? []).map((sec, sIdx) =>
                (sec.sectionIndex ?? sIdx) !== row.sectionIndex
                  ? sec
                  : {
                      ...sec,
                      items: (sec.items ?? []).map(it => (it.id === row.itemId ? { ...it, status: nextStatus } : it)),
                    }
              ),
            }
      )
    )
  }

  const toggleItem = (row: any) => {
    const nextDone = !row.done
    toggleItemLocalEverywhere(row, nextDone)
    // Track pending
    upsertPending(
      { PK: row.pk, SK: row.sk, itemId: row.itemId },
      { sectionIndex: row.sectionIndex, itemIndex: row.itemIndex ?? 0, newStatus: nextDone ? 'Done' : 'Not Started' }
    )
  }

  /** Toggle an entire section (optimistic in local + cache, mark pending for all) */
  const toggleSection = (sec: { rows: typeof rows[0][] }, nextDone: boolean) => {
    const nextStatus = nextDone ? 'Done' : 'Not Started'

    // Local
    setLocalLists(prev =>
      prev.map(ch => {
        const affected = sec.rows.filter(r => r.pk === ch.PK && r.sk === ch.SK)
        if (!affected.length) return ch
        const sectionIdx = affected[0].sectionIndex
        return {
          ...ch,
          sections: (ch.sections ?? []).map((s, sIdx) =>
            (s.sectionIndex ?? sIdx) !== sectionIdx
              ? s
              : {
                  ...s,
                  items: (s.items ?? []).map(it => ({ ...it, status: nextStatus })),
                }
          ),
        }
      })
    )

    // Cache
    patchChecklistsInCache((prevLists: Checklist[] = []) =>
      (prevLists ?? []).map(ch => {
        const affected = sec.rows.filter(r => r.pk === ch.PK && r.sk === ch.SK)
        if (!affected.length) return ch
        const sectionIdx = affected[0].sectionIndex
        return {
          ...ch,
          sections: (ch.sections ?? []).map((s, sIdx) =>
            (s.sectionIndex ?? sIdx) !== sectionIdx
              ? s
              : {
                  ...s,
                  items: (s.items ?? []).map(it => ({ ...it, status: nextStatus })),
                }
          ),
        }
      })
    )

    mirrorParent((prevLists: Checklist[]) =>
      (prevLists ?? []).map(ch => {
        const affected = sec.rows.filter(r => r.pk === ch.PK && r.sk === ch.SK)
        if (!affected.length) return ch
        const sectionIdx = affected[0].sectionIndex
        return {
          ...ch,
          sections: (ch.sections ?? []).map((s, sIdx) =>
            (s.sectionIndex ?? sIdx) !== sectionIdx
              ? s
              : {
                  ...s,
                  items: (s.items ?? []).map(it => ({ ...it, status: nextStatus })),
                }
          ),
        }
      })
    )

    // Track pendings for all rows in section
    setPending(prev => {
      const next = [...prev]
      sec.rows.forEach(r => {
        const idx = next.findIndex(p => p.PK === r.pk && p.SK === r.sk && p.itemId === r.itemId)
        const rec = {
          PK: r.pk,
          SK: r.sk,
          itemId: r.itemId,
          sectionIndex: r.sectionIndex,
          itemIndex: r.itemIndex ?? 0,
          newStatus: nextStatus,
        }
        if (idx === -1) next.push(rec)
        else next[idx] = rec
      })
      return next
    })
  }

  // ===== Save / Cancel =====
  const onSave = async () => {
    // 1) save checklist changes (unchanged)
    if (pending.length) {
      const listsNow = localLists
      const withRealIndexes = pending
        .map(p => ({ ...p, itemIndex: findItemIndexById(listsNow, p.PK, p.SK, p.sectionIndex, p.itemId) }))
        .filter(p => p.itemIndex >= 0)

      const byPath = new Map<string, typeof withRealIndexes[number]>()
      for (const u of withRealIndexes) {
        byPath.set(`${u.PK}|${u.SK}|${u.sectionIndex}|${u.itemIndex}`, u)
      }
      const finalUpdates = Array.from(byPath.values()).map(u => ({
        PK: u.PK,
        SK: u.SK,
        itemId: u.itemId,
        fieldName: 'status',
        newStatus: u.newStatus,
        sectionIndex: u.sectionIndex,
        itemIndex: u.itemIndex,
      }))

      await batchUpdate(finalUpdates)
      setPending([])
    }

    // 2) save field drafts
    if (draftDirtyKeys.length && organization?.PK && organization?.SK) {
      const updates = draftDirtyKeys.map(k => ({
        tableName: 'ORGANIZATIONS_TABLE',
        PK: organization.PK,
        SK: organization.SK,
        fieldName: k,
        newValue: fieldDraft[k],
      }))

      await batchUpdateItems({ updates })
      setFieldPristine(fieldDraft)
      // patch org in cache
      const patch: Record<string, any> = {}
      draftDirtyKeys.forEach(k => (patch[k] = fieldDraft[k]))
      syncOrganizationsCache(qc, { ...organization, ...patch })

      // new pristine baseline AFTER save
    }

    onSaved?.()
  }

  const onCancel = () => {
    // checklist revert (unchanged)
    const pristine = pristineRef.current ?? []
    setLocalLists(pristine)
    patchChecklistsInCache(prev => mergeRestoreSubset(prev, pristine))
    mirrorParentMerge(pristine)
    setPending([])

    // fields revert
    setFieldDraft(fieldDraft)
  }

  const refetchThisDealChecklists = () => {
    qc.invalidateQueries({
      predicate: q => Array.isArray(q.queryKey) && String(q.queryKey[0]).includes('checklists'),
    })
  }

  const expanded_height = expanded ? 'calc(100vh - 210px)' : 'calc(100vh - 80px)'
  const pinned_height = `calc(${expanded_height} - 50px)`
  const calc_height = pinned ? pinned_height : expanded_height
  const isCheckDisabled = (type === 'integration' && !IS_INTEGRATION) || (type === 'seo' && !IS_SEO)
  return (
    <Box sx={{ position: 'relative' }}>
      {/* sticky save bar */}
      <Slide direction="up" in={dirty} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 2,
            bgcolor: 'background.paper',
            borderBottom: theme => `1px solid ${theme.palette.divider}`,
            px: 2,
            py: 1,
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="body2">
              {changeCount} change{changeCount === 1 ? '' : 's'} not saved
            </Typography>
            <Box>
              <Button size="small" onClick={onCancel}>
                Cancel
              </Button>
              <Button size="small" variant="contained" onClick={onSave}>
                Save
              </Button>
            </Box>
          </Stack>
        </Box>
      </Slide>
      {!checklistData || (!checklistData?.length && <Typography>No Checklists Found</Typography>)}
      {checklistData && checklistData?.length > 0 && (
        <Grid container>
          <Grid item xs={12} md={type && type === 'integration' ? 9 : 12}>
            <TableContainer
              component={Paper}
              // sx={{
              //   height: expanded ? 'calc(70vh - 180px)' : 'calc(80vh - 10px)',
              //   maxHeight: '80vh',
              //   transition: 'height 0.3s ease',
              // }}
            >
              <BoxScroller
                sx={{
                  height: calc_height,
                  maxHeight: '100vh',
                  transition: 'height 0.3s ease',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, py: 1 }}>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress variant="determinate" value={progress.pct} />
                  </Box>
                  <Typography variant="body2">{progress.pct}%</Typography>
                  <Tooltip title="Refetch checklists" placement="left">
                    <div>
                      <IconButton onClick={refetchThisDealChecklists} size="small" disabled={isCheckDisabled}>
                        <RefreshIcon />
                      </IconButton>
                    </div>
                  </Tooltip>
                </Box>

                <Table
                  size="small"
                  stickyHeader
                  className="nco-table"
                  sx={{ '& .MuiTableCell-root': { textAlign: 'left' } }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>Task</TableCell>
                      <TableCell width={120} align="center">
                        Done
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sections.map(sec => {
                      const secTotal = sec.rows.length
                      const secDone = sec.rows.filter(r => r.done).length
                      const secAllDone = secTotal > 0 && secDone === secTotal
                      return (
                        <React.Fragment key={sec.key}>
                          <TableRow selected>
                            <TableCell>
                              <Typography fontWeight={700} sx={{ color: 'text.warning' }}>
                                {sec.title}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Checkbox
                                checked={secAllDone}
                                indeterminate={!secAllDone && secDone > 0}
                                onChange={() => toggleSection(sec, !secAllDone)}
                                disabled={isCheckDisabled}
                                sx={{
                                  color: 'text.secondary', // unchecked
                                  '&.Mui-checked': { color: 'success.main' }, // checked
                                  '&.Mui-disabled': { color: 'text.disabled' },
                                  // optional: tweak icon size
                                  '& .MuiSvgIcon-root': {
                                    fontSize: 22,
                                    color: isCheckDisabled ? 'text.disabled' : 'text.primary',
                                  },
                                }}
                              />
                            </TableCell>
                          </TableRow>

                          {sec.rows.map(row => (
                            <TableRow key={`${row.pk}_${row.sk}_${row.sectionIndex}_${row.itemId}`} hover>
                              <TableCell>{row.title}</TableCell>
                              <TableCell align="center">
                                <Checkbox
                                  checked={row.done}
                                  onChange={() => toggleItem(row)}
                                  disabled={isCheckDisabled}
                                  sx={{
                                    color: 'text.secondary', // unchecked
                                    '&.Mui-checked': { color: 'success.main' }, // checked

                                    // optional: tweak icon size
                                    '& .MuiSvgIcon-root': {
                                      fontSize: 22,
                                      color: isCheckDisabled ? 'text.disabled' : 'text.primary',
                                    },
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}

                          <TableRow>
                            <TableCell colSpan={5} sx={{ py: 0 }}>
                              {/* <Divider /> */}
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      )
                    })}
                  </TableBody>
                </Table>
              </BoxScroller>
            </TableContainer>
          </Grid>
          {type && type === 'integration' && (
            <Grid item xs={12} md={3} px={1}>
              <IntegrationFields
                deal={deal}
                organization={organization}
                formData={fieldDraft}
                onInputChange={handleFieldInputChange}
                expanded={expanded}
                isDisabled={isCheckDisabled}
              />
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  )
}
